// One Hebrew date formatter for the whole app.
//
// This existed six times — a split/reverse/join in CalendarTab, a manual
// d/m split in MarketWeekCard, and four separate toLocaleDateString calls with
// four different option objects. The outputs disagreed on separator, on whether
// the year appeared, and on how the month was written, so the same day read
// four ways depending on which screen you were on.
//
// The goal here is one source, not one format: a calendar cell and a news
// masthead genuinely need different lengths. So there are three explicit
// functions, each named for how much it says, and callers pick the one that
// fits rather than passing a format string around.
//
//   dateNumeric  11/08/2026   ·  11/08 without the year
//   dateShort    11 באוג׳     ·  יום ג׳, 11 באוג׳ with the weekday
//   dateFull     יום שלישי, 11 באוגוסט 2026
//
// Accepts a Date, a millisecond timestamp, a full ISO string, or a date-only
// "YYYY-MM-DD". Anything unparsable returns "" so the caller renders nothing
// rather than "Invalid Date".

// A date-only string has no time zone, and `new Date("2026-08-11")` parses it
// as midnight UTC — which is the day before in any negative offset, and can
// shift the weekday for a viewer east of UTC too. Anchoring at noon UTC keeps
// the calendar day intact everywhere on earth.
function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string" && value) {
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const locale = (lang) => (lang === "en" ? "en-US" : "he-IL");

// Digits only. `year: false` gives the compact 11/08 used where the row is
// tight and the year is obvious from context.
// `weekday: true` leads with the day's name ("שני 28/09/2026").
// DD/MM/YYYY is the app's one date format (2026-09-26); dateShort and dateFull
// remain for the rare place that spells a month out.
const WEEKDAY_HE = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
export function dateNumeric(value, { lang = "he", year = true, weekday = false } = {}) {
  const d = toDate(value);
  if (!d) return "";
  const opts = { day: "2-digit", month: "2-digit" };
  if (year) opts.year = "numeric";
  const out = d.toLocaleDateString(locale(lang), opts).replace(/\./g, "/");
  if (!weekday) return out;
  const wd = lang === "en" ? d.toLocaleDateString("en-US", { weekday: "short" }) : WEEKDAY_HE[d.getDay()];
  return `${wd} ${out}`;
}

// Day and abbreviated month, optionally led by the abbreviated weekday.
export function dateShort(value, { lang = "he", weekday = false } = {}) {
  const d = toDate(value);
  if (!d) return "";
  const opts = { day: "numeric", month: "short" };
  if (weekday) opts.weekday = "short";
  return d.toLocaleDateString(locale(lang), opts);
}

// Everything spelled out — for a masthead or a single prominent date.
// `weekday: false` drops the day name where the date stands alone in a
// sentence ("אובחן ב-11 באוגוסט 2026") and the weekday would only pad it.
export function dateFull(value, { lang = "he", weekday = true } = {}) {
  const d = toDate(value);
  if (!d) return "";
  const opts = { day: "numeric", month: "long", year: "numeric" };
  if (weekday) opts.weekday = "long";
  return d.toLocaleDateString(locale(lang), opts);
}

// ─── A headline that says "today" ───────────────────────────────────────────
// "Apple rises today" read on Saturday is about Friday. When a headline says
// today (either language), the screen shows WHEN it was published next to it
// (2026-09-26). → "פורסם DD/MM HH:MM", or null when the headline does not
// say today or the time is unknown.
const TODAY_RE = /(^|[^\u0590-\u05FFa-z])(today|tonight|היום|הערב)([^\u0590-\u05FFa-z]|$)/i;
export function publishedNote(headline, at) {
  if (!headline || !TODAY_RE.test(headline) || !at) return null;
  const d = new Date(typeof at === "number" && at < 1e12 ? at * 1000 : at);
  if (!Number.isFinite(d.getTime())) return null;
  const p2 = n => String(n).padStart(2, "0");
  return `פורסם ${p2(d.getDate())}/${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

// ─── "נכון ל-DD/MM HH:MM" ─────────────────────────────────────────────────────
// When a figure was true: the date and hour when both are known, the date
// alone when only a date is (an end-of-day series). null when neither is.
export function asOfNote(at, date) {
  const p2 = n => String(n).padStart(2, "0");
  if (at) {
    const d = new Date(typeof at === "number" && at < 1e12 ? at * 1000 : at);
    if (Number.isFinite(d.getTime())) return `נכון ל-${p2(d.getDate())}/${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || "");
  return m ? `נכון ל-${m[3]}/${m[2]}` : null;
}
